import React from 'react';
import { Link } from 'react-router-dom';

/* ============================================================
   Legal — Privacy Policy, Terms of Use, and a plain-English
   "AI & Data" transparency page. Public routes (/privacy,
   /terms, /transparency), reachable signed in or out.

   The copy is written to match what the app ACTUALLY does —
   the real Supabase backend and the real AI/third-party
   processors (Anthropic, xAI, Google, Open Food Facts,
   Netlify). It is an honest starting point, NOT a substitute
   for review by a lawyer before launch.
============================================================ */

const UPDATED = 'June 15, 2026';
const CONTACT = 'feedback@fitflex.club';

type DocId = 'privacy' | 'terms' | 'transparency';

const A: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-white">{children}</a>
);

const Section: React.FC<{ n?: string; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <section className="lg-sec">
    <h2 className="lg-h2">{n && <span className="lg-num">{n}</span>}{title}</h2>
    <div className="lg-body">{children}</div>
  </section>
);

const UL: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="lg-ul">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
);

/* ---------------- Privacy Policy ---------------- */

const Privacy: React.FC = () => (
  <>
    <Section title="The short version">
      <p>
        FitFlex is a tool coaches use to plan training, track their clients, and grow their business.
        To do that we store the information you and your coach put in, and — only when you ask for an
        AI feature — we send a small, relevant slice of it to the AI provider that powers that feature.
        We don't sell your data, and we don't show ads. The plain-English breakdown of what goes where
        lives on our <Link to="/transparency" className="underline underline-offset-2 hover:text-white">AI &amp; Data</Link> page.
      </p>
    </Section>

    <Section n="1" title="Who we are">
      <p>
        FitFlex ("FitFlex", "we", "us") provides coaching software at fitflex.club. If you have any
        question about this policy or your data, email <A href={`mailto:${CONTACT}`}>{CONTACT}</A>.
      </p>
    </Section>

    <Section n="2" title="The information we hold">
      <p>Depending on how you use FitFlex, that can include:</p>
      <UL items={[
        <><strong>Your account</strong> — your name and email, and (if you sign in with Google) the basic profile Google shares.</>,
        <><strong>Client records a coach creates</strong> — names, contact details, training history, goals, injuries, coaching notes, and weekly check-in numbers (such as weight, body-fat %, and muscle mass).</>,
        <><strong>Files you upload</strong> — spreadsheets or training-log workbooks you import. These are parsed in your browser; the full file is not sent to us or to any AI unless you trigger an AI feature (see §4).</>,
        <><strong>Transformation photos</strong> — images a coach uploads for a client, stored privately (never public).</>,
        <><strong>Apple Health nutrition</strong> — on iOS, if you choose to connect Apple Health, FitFlex reads your daily nutrition (calories, protein, carbohydrate, and fat) so meals you log in MyFitnessPal or a similar app appear here without re-entry. It is read-only, you can turn it off anytime in the app or in iOS Settings, and it is covered in §9.</>,
        <><strong>A calendar address</strong> — if a coach connects a Google Calendar, the secret iCal URL they paste, used only to read events that match their clients.</>,
        <><strong>Basic usage data</strong> — standard server and security logs from our hosting provider.</>,
      ]} />
    </Section>

    <Section n="3" title="How we use it">
      <UL items={[
        'To run the features you use — plan weeks, log sessions, show trends, send calendar invites, and so on.',
        'To provide the optional AI features you choose to run (import mapping, the Advisor, nutrition lookup).',
        'To keep accounts secure and the service working, and to respond when you contact us.',
      ]} />
      <p>We do not use your data for advertising, and we do not sell it.</p>
    </Section>

    <Section n="4" title="The services that process data for us (sub-processors)">
      <p>
        We use a small number of trusted providers to run FitFlex. Each processes only what its job
        requires, under its own terms:
      </p>
      <UL items={[
        <><strong>Supabase</strong> — our database, login, file storage, and serverless functions (United States). It holds essentially everything above. <A href="https://supabase.com/privacy">Privacy</A>.</>,
        <><strong>Anthropic (Claude)</strong> — powers <em>AI import mapping</em>. When you import a sheet, only the column headers and a few sample rows (capped at roughly 10&nbsp;KB) are sent so Claude can suggest a mapping. <A href="https://www.anthropic.com/legal/privacy">Privacy</A>.</>,
        <><strong>xAI (Grok)</strong> — powers the <em>Advisor</em>. When you ask a question, a computed summary of that client's training data plus your question are sent to generate the answer. <A href="https://x.ai/legal/privacy-policy">Privacy</A>.</>,
        <><strong>Open Food Facts</strong> — powers <em>nutrition search</em>. Only the food term you type is sent. <A href="https://world.openfoodfacts.org/terms-of-use">Terms</A>.</>,
        <><strong>Google</strong> — optional sign-in, the optional Drive file picker, and optional calendar sync. Used only when you choose those. <A href="https://policies.google.com/privacy">Privacy</A>.</>,
        <><strong>Netlify</strong> — hosting and content delivery for the website. <A href="https://www.netlify.com/privacy/">Privacy</A>.</>,
      ]} />
      <p>
        These AI providers process your data to generate a response under their API terms. We do not
        grant them rights to use your data to train their models beyond what those standard API terms
        allow. The <Link to="/transparency" className="underline underline-offset-2 hover:text-white">AI &amp; Data</Link> page
        spells out each flow.
      </p>
    </Section>

    <Section n="5" title="Coaches, clients, and consent">
      <p>
        When a coach adds a client and uploads their information, the <strong>coach</strong> decides what
        to collect and is responsible for having the right to do so — including consent to store a
        client's health information and transformation photos, and consent before publishing any case
        study. FitFlex processes that information on the coach's behalf to provide the service.
      </p>
    </Section>

    <Section n="6" title="Security & storage">
      <UL items={[
        'Data is transmitted over encrypted (HTTPS) connections.',
        'Database access is governed by row-level security, so each coach can reach only their own data and their own clients.',
        'Transformation photos live in a private storage bucket and are served through short-lived signed links — they are never publicly browsable.',
        'Our data is hosted in the United States.',
      ]} />
    </Section>

    <Section n="7" title="Keeping & deleting data">
      <p>
        We keep your data while your account is active. You can delete records inside the app, and you
        can ask us to delete your account and its data by emailing <A href={`mailto:${CONTACT}`}>{CONTACT}</A>.
        Some information may persist briefly in backups or security logs before it ages out.
      </p>
    </Section>

    <Section n="8" title="Your rights">
      <p>
        Depending on where you live, you may have rights to access, correct, export, or delete your
        personal information, and to object to certain processing. To exercise any of these, email
        us. We don't charge for reasonable requests.
      </p>
    </Section>

    <Section n="9" title="Health information">
      <p>
        Some of what coaches track (weight, body composition, injuries, photos) is sensitive. We treat
        it with care, but FitFlex is general fitness-coaching software — it is not a medical service and
        is not intended to create a HIPAA-covered relationship. Coaches should not store information they
        aren't authorized to hold.
      </p>
      <p>
        <strong>Apple Health (HealthKit).</strong> On iOS you can connect Apple Health so FitFlex reads
        your nutrition data — daily calories, protein, carbohydrate, and fat. We ask for read-only
        access to those types and nothing else; FitFlex does not write to Apple Health. We use this data
        only to show your intake in the app and to your coach inside your coaching relationship. In line
        with Apple's HealthKit requirements:
      </p>
      <UL items={[
        'We never use Health data for advertising or marketing, and we never use it for data-mining.',
        'We never sell Health data, and we never share it with third parties for advertising or data-mining.',
        'Health-derived nutrition is not sent to any AI provider (see the AI & Data page).',
        'It is stored under the same row-level security as the rest of your data — reachable only by you and your coach.',
        'You can disconnect at any time in the app, or revoke access in iOS Settings › Health › Data Access & Devices; we then stop reading new data.',
      ]} />
    </Section>

    <Section n="10" title="Children">
      <p>FitFlex isn't intended for anyone under 16, and we don't knowingly collect their information.</p>
    </Section>

    <Section n="11" title="Changes">
      <p>
        FitFlex is in active beta and evolving quickly; we may update this policy. We'll revise the date
        at the top, and significant changes will be highlighted in the app.
      </p>
    </Section>
  </>
);

/* ---------------- Terms of Use ---------------- */

const Terms: React.FC = () => (
  <>
    <Section title="Beta, in plain terms">
      <p>
        FitFlex is in active beta. That means it's improving fast, features may change or break, and you
        should keep your own backup of anything you can't afford to lose. We're grateful you're here
        early — and these terms set out the deal between us.
      </p>
    </Section>

    <Section n="1" title="Accepting these terms">
      <p>By creating an account or using FitFlex, you agree to these Terms of Use and to our <Link to="/privacy" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>. If you don't agree, please don't use FitFlex.</p>
    </Section>

    <Section n="2" title="Your account">
      <UL items={[
        'You must be at least 18 (or the age of majority where you live) and provide accurate information.',
        "You're responsible for activity under your account and for keeping your login secure.",
        'You may invite clients; you remain responsible for the data you bring into FitFlex about them.',
      ]} />
    </Section>

    <Section n="3" title="Acceptable use">
      <p>Don't use FitFlex to:</p>
      <UL items={[
        'break the law, or upload data you don\'t have the right to use;',
        "infringe anyone's privacy, intellectual property, or other rights;",
        'attempt to disrupt, reverse-engineer, scrape, or overload the service;',
        'upload malware or attempt to access data that isn\'t yours.',
      ]} />
    </Section>

    <Section n="4" title="Your content stays yours">
      <p>
        You own the data and content you put into FitFlex. You grant us the limited permission needed to
        host, process, and display it back to you so we can run the features you use — including sending
        the relevant slices to the AI providers described in the <Link to="/transparency" className="underline underline-offset-2 hover:text-white">AI &amp; Data</Link> page
        when you trigger an AI feature. That permission ends when you delete the content or your account.
      </p>
    </Section>

    <Section n="5" title="Client data & permission">
      <p>
        If you're a coach, you confirm you have the right and any necessary consent to upload your
        clients' information and photos, and to share a case study before you publish it. You're
        responsible for how you use what FitFlex helps you create.
      </p>
    </Section>

    <Section n="6" title="AI features are assistance, not advice">
      <p>
        The Advisor and other AI features produce suggestions and summaries from your data and, in
        research mode, from public sources. They can be wrong or incomplete. Treat them as a starting
        point — review before acting on them, and apply your own professional judgment.
      </p>
    </Section>

    <Section n="7" title="Not medical advice">
      <p>
        FitFlex is fitness-coaching software, not a medical, nutritional, or healthcare provider. Nothing
        in it is medical advice. Coaches and clients should consult a qualified professional for health
        decisions.
      </p>
    </Section>

    <Section n="8" title="Beta, availability & changes">
      <p>
        FitFlex is provided as-is during beta. We don't promise uninterrupted availability, and we may
        add, change, or remove features. We'll try not to lose your data, but you should keep your own
        backups of anything critical.
      </p>
    </Section>

    <Section n="9" title="Fees">
      <p>
        Core features are free during beta. Some features are marked "Premium" and are free during beta
        as well. If we introduce paid plans, we'll tell you the price before you're charged.
      </p>
    </Section>

    <Section n="10" title="Disclaimers & liability">
      <p>
        To the fullest extent allowed by law, FitFlex is provided without warranties of any kind, and we
        aren't liable for indirect or consequential damages, or for lost data or profits. Our total
        liability for any claim is limited to the amount you paid us in the 12 months before it arose
        (which, during the free beta, is zero). Some places don't allow these limits, so parts may not
        apply to you.
      </p>
    </Section>

    <Section n="11" title="Termination">
      <p>
        You can stop using FitFlex and delete your account at any time. We may suspend or end accounts
        that breach these terms or put the service or others at risk.
      </p>
    </Section>

    <Section n="12" title="Governing law">
      <p>
        These Terms are governed by the laws of the State of California, United States, without regard
        to its conflict-of-law rules. The state and federal courts located in California will have
        exclusive jurisdiction over any dispute arising from these Terms or your use of FitFlex.
      </p>
    </Section>

    <Section n="13" title="Contact">
      <p>Questions about these terms? Email <A href={`mailto:${CONTACT}`}>{CONTACT}</A>.</p>
    </Section>
  </>
);

/* ---------------- AI & Data transparency ---------------- */

const Transparency: React.FC = () => (
  <>
    <Section title="What leaves your device, and where it goes">
      <p>
        FitFlex uses AI in a few specific, opt-in places. We think you should know exactly what each one
        sends. The rule across all of them: <strong>the AI proposes, you decide</strong>, and only the
        smallest useful slice of data is sent — never your whole sheet, never your whole database.
      </p>
    </Section>

    <Section title="AI import mapping">
      <p><span className="lg-tag">Powered by Anthropic (Claude)</span></p>
      <UL items={[
        'When you import a spreadsheet, your browser reads the whole file locally and figures out a first-pass mapping on its own.',
        'Only the column headers and a few sample rows — capped at about 10 KB — are sent to Claude to refine that mapping. The rest of your sheet never leaves your device.',
        'The result is a suggestion you review and approve before anything is saved.',
        'There is a daily limit per coach, and the feature simply turns off if it isn\'t available — your import still works manually.',
      ]} />
    </Section>

    <Section title="The Advisor">
      <p><span className="lg-tag">Powered by xAI (Grok)</span></p>
      <UL items={[
        "When you ask the Advisor about a client, FitFlex computes a compact summary of that client's data — their training schedule, the dates of personal records, check-in numbers, and short note excerpts — and sends it with your question.",
        'Raw figures are computed by FitFlex, not invented by the AI; the model interprets them, it doesn\'t make up your numbers.',
        'If you turn on "research," the Advisor also runs web searches to find and cite published sources. Without it, no web search happens.',
        'Conversations and saved reports are stored with the client they belong to, visible only to you.',
      ]} />
    </Section>

    <Section title="Nutrition search">
      <p><span className="lg-tag">Powered by Open Food Facts</span></p>
      <p>When you search for a food, only the term you type is sent to look up its nutrition facts. Nothing about you or your clients is included.</p>
    </Section>

    <Section title="What we don't do">
      <UL items={[
        'We don\'t send your data to AI unless you trigger a feature that needs it.',
        'We don\'t send Apple Health data to any AI provider. Health-derived nutrition stays between you and your coach and is never part of an Advisor prompt.',
        'We don\'t sell your data or use it for advertising.',
        'We don\'t grant AI providers rights to train their models on your data beyond their standard API terms — which, for the API products we use, do not use inputs to train their public models.',
      ]} />
    </Section>

    <Section title="Your controls">
      <UL items={[
        'AI features are opt-in — you choose when to run them.',
        'Daily usage caps keep things bounded.',
        'You can delete reports, conversations, and client records in the app, or ask us to delete your account entirely.',
      ]} />
      <p>
        Full detail on data handling is in our <Link to="/privacy" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>.
        Questions? <A href={`mailto:${CONTACT}`}>{CONTACT}</A>.
      </p>
    </Section>
  </>
);

/* ---------------- shell ---------------- */

const DOCS: Record<DocId, { title: string; tag: string; body: React.FC }> = {
  privacy: { title: 'Privacy Policy', tag: 'Privacy', body: Privacy },
  terms: { title: 'Terms of Use', tag: 'Terms', body: Terms },
  transparency: { title: 'AI & Data', tag: 'Transparency', body: Transparency },
};

const Legal: React.FC<{ doc: DocId }> = ({ doc }) => {
  const meta = DOCS[doc];
  const Body = meta.body;
  return (
    <div className="lg">
      <nav className="lg-nav">
        <Link to="/" className="app-header-brand text-[17px] no-underline">FITFLEX</Link>
        <Link to="/" className="text-[13px] text-fit-text-secondary hover:text-white no-underline">← Home</Link>
      </nav>

      <main className="lg-main">
        <p className="mono-label mb-2">{meta.tag}</p>
        <h1 className="lg-h1">{meta.title}</h1>
        <p className="lg-updated">Last updated {UPDATED} · FitFlex is in active beta.</p>

        <Body />

        <div className="lg-cross">
          {(Object.keys(DOCS) as DocId[]).map((d) => (
            <Link
              key={d}
              to={`/${d}`}
              className={`lg-cross-link no-underline ${d === doc ? 'lg-cross-link--on' : ''}`}
            >
              {DOCS[d].title}
            </Link>
          ))}
        </div>
      </main>

      <footer className="lg-footer">
        <span className="mono-label">FITFLEX</span>
        <span className="text-[12px] text-fit-muted">Built for coaches, by coaches.</span>
      </footer>

      <style>{`
        .lg { min-height: 100vh; background: var(--bg-primary); }
        .lg a.no-underline { text-decoration: none; }
        .lg-nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px clamp(16px, 5vw, 48px);
          border-bottom: 1px solid var(--line);
        }
        .lg-main { max-width: 720px; margin: 0 auto; padding: clamp(40px, 7vw, 80px) clamp(16px, 5vw, 24px) 64px; }
        .lg-h1 { font-size: clamp(30px, 5vw, 46px); font-weight: 800; letter-spacing: -0.04em; line-height: 1.05; }
        .lg-updated { font-size: 13px; color: var(--text-muted); margin-top: 12px; margin-bottom: 8px; }
        .lg-sec { border-top: 1px solid var(--line); padding: 26px 0 4px; }
        .lg-sec:first-of-type { border-top: none; }
        .lg-h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 12px; display: flex; gap: 10px; align-items: baseline; }
        .lg-num { font-size: 12px; font-weight: 700; color: var(--text-muted); font-variant-numeric: tabular-nums; min-width: 18px; }
        .lg-body { color: var(--text-secondary); font-size: 15px; line-height: 1.7; }
        .lg-body p { margin-bottom: 14px; }
        .lg-body strong { color: var(--text-primary); font-weight: 600; }
        .lg-body em { font-style: normal; color: var(--text-primary); }
        .lg-ul { list-style: none; padding: 0; margin: 0 0 14px; display: flex; flex-direction: column; gap: 9px; }
        .lg-ul li { position: relative; padding-left: 18px; }
        .lg-ul li::before { content: '·'; position: absolute; left: 4px; color: var(--text-muted); }
        .lg-tag {
          display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
          text-transform: uppercase; color: var(--text-muted);
          border: 1px solid var(--line-strong); border-radius: 999px; padding: 3px 10px; margin-bottom: 4px;
        }
        .lg-cross { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 40px; padding-top: 24px; border-top: 1px solid var(--line); }
        .lg-cross-link {
          font-size: 12px; font-weight: 600; color: var(--text-secondary);
          border: 1px solid var(--line); border-radius: 999px; padding: 6px 14px;
          transition: color 0.15s ease, border-color 0.15s ease;
        }
        .lg-cross-link:hover { color: var(--text-primary); border-color: var(--line-strong); }
        .lg-cross-link--on { color: var(--text-primary); border-color: var(--line-strong); background: rgba(255,255,255,0.05); }
        .lg-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 22px clamp(16px, 5vw, 48px); border-top: 1px solid var(--line);
        }
      `}</style>
    </div>
  );
};

export default Legal;
